import * as signalR from "@microsoft/signalr";
import { getUserToken } from "../../../utils/tokenUtils";

/**
 * /hubs/livechat için ayrı bir bağlantı.
 *
 * signalRService sipariş hub'ına bağlıdır ve ActiveOrderBarSignalR unmount olurken
 * stop() çağırır; aynı örneği paylaşmak sipariş takibini koparırdı. Bu yüzden canlı
 * destek kendi bağlantısını yönetir.
 */
const resolveHubUrl = (): string => {
  const explicit = process.env.REACT_APP_LIVECHAT_HUB_URL;
  if (explicit) return explicit;

  // Sipariş hub'ı tanımlıysa aynı host üzerinden türet.
  const orderHub = process.env.REACT_APP_SIGNALR_HUB_URL;
  if (orderHub) return orderHub.replace(/\/hubs\/order\/?$/i, "/hubs/livechat");

  return "https://service.mutfacikservices.com/hubs/livechat";
};

const HUB_URL = resolveHubUrl();

class LiveChatHubService {
  private connection: signalR.HubConnection | null = null;
  private starting: Promise<void> | null = null;
  private reconnectCallback: (() => void) | null = null;

  private buildConnection(): signalR.HubConnection {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => getUserToken()?.token ?? "",
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000, 60000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Tek bir iç işleyici bağlanır; dışarıdaki geri çağrı setOnReconnected ile
    // değiştirilir. Böylece yeniden bağlanma işleyicisi hiç birikmez.
    connection.onreconnected(() => this.reconnectCallback?.());

    return connection;
  }

  /** Aynı anda birden fazla start() çağrısı tek bağlantıya indirgenir. */
  async start(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) return;
    if (this.starting) return this.starting;

    if (!this.connection) {
      this.connection = this.buildConnection();
    }

    this.starting = this.connection
      .start()
      .catch((err) => {
        console.error("[LiveChat] Bağlantı kurulamadı:", err);
      })
      .finally(() => {
        this.starting = null;
      });

    return this.starting;
  }

  async stop(): Promise<void> {
    if (!this.connection) return;
    try {
      await this.connection.stop();
    } catch {
      /* kapanış hatası yutulur */
    }
    this.connection = null;
    this.starting = null;
    this.reconnectCallback = null;
  }

  on<T extends unknown[]>(event: string, callback: (...args: T) => void): void;
  on<T>(event: string, callback: (data: T) => void): void;
  on(event: string, callback: (...args: unknown[]) => void): void {
    this.connection?.on(event, callback);
  }

  off(event: string, callback?: (...args: unknown[]) => void): void {
    if (!this.connection) return;
    if (callback) this.connection.off(event, callback);
    else this.connection.off(event);
  }

  /** Yeniden bağlanma geri çağrısını değiştirir (biriktirmez). */
  setOnReconnected(callback: (() => void) | null): void {
    this.reconnectCallback = callback;
  }

  /** Sohbet odasına katılır. Sunucu katılımcılığı veritabanından doğrular. */
  async joinConversation(conversationId: string): Promise<void> {
    await this.invoke("JoinConversation", conversationId);
  }

  async leaveConversation(conversationId: string): Promise<void> {
    await this.invoke("LeaveConversation", conversationId);
  }

  /** Yazıyor göstergesi — çağıran taraf debounce etmeli. */
  async notifyTyping(conversationId: string, isTyping: boolean): Promise<void> {
    await this.invoke("NotifyTyping", conversationId, isTyping);
  }

  private async invoke(method: string, ...args: unknown[]): Promise<void> {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) return;
    try {
      await this.connection.invoke(method, ...args);
    } catch (err) {
      console.error(`[LiveChat] ${method} başarısız:`, err);
    }
  }

  get state(): signalR.HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

const liveChatHub = new LiveChatHubService();
export default liveChatHub;
